import { Copy, Link2 } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { Button } from "@fox1ck-org/ui";
import { stripAnsi } from "./ansi";
import { formatLogTimeAbsolute } from "./levelStyles";
import type { LogRecord } from "./types";

interface LogDetailPanelProps {
  record: LogRecord;
  onCorrelationClick?: (id: string) => void;
}

/**
 * Expanded view rendered below an active LogLine. Shows the full message
 * (de-ANSI'd for readability), correlation ID drilldown link, structured
 * context fields, and copy actions.
 */
export function LogDetailPanel({ record, onCorrelationClick }: LogDetailPanelProps) {
  const cleanMessage = stripAnsi(record.msg);
  const contextEntries = Object.entries(record.context ?? {});

  const copy = (label: string, value: string) => {
    void navigator.clipboard.writeText(value);
    toast.success(`Скопировано: ${label}`);
  };

  const copyAsJson = () => {
    const payload = {
      ts: record.ts,
      level: record.level,
      pod: record.pod,
      container: record.container,
      namespace: record.namespace,
      logger: record.logger,
      correlationId: record.correlationId,
      msg: cleanMessage,
      context: record.context,
    };
    void navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success("Скопировано как JSON");
  };

  return (
    <div className="border-l-2 border-l-primary/30 bg-card/40 px-4 py-3 space-y-3 text-xs">
      {/* Full message */}
      <div className="relative">
        <pre className="font-mono text-[12px] text-foreground bg-background/60 border rounded-md p-3 whitespace-pre-wrap break-all max-h-[40vh] overflow-auto">
          {cleanMessage}
        </pre>
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-1.5 right-1.5 h-6 w-6"
          onClick={() => copy("message", cleanMessage)}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>

      {/* Correlation ID drilldown */}
      {record.correlationId && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Трассировка</span>
          <button
            type="button"
            onClick={() => onCorrelationClick?.(record.correlationId!)}
            className="font-mono text-primary hover:text-primary/80 inline-flex items-center gap-1"
          >
            <Link2 className="h-3 w-3" />
            {record.correlationId}
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={() => copy("ID корреляции", record.correlationId!)}
          >
            <Copy className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Context */}
      {contextEntries.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
            Контекст
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {contextEntries.map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline gap-2 rounded border border-border/60 bg-background/40 px-2 py-1"
              >
                <span className="text-muted-foreground shrink-0">{k}</span>
                <span className="font-mono text-foreground truncate">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-muted-foreground">
        <Meta label="Время" value={formatLogTimeAbsolute(record.ts)} />
        <Meta label="Под" value={record.pod || "—"} />
        {record.container && <Meta label="Контейнер" value={record.container} />}
        {record.namespace && <Meta label="Пространство имён" value={record.namespace} />}
        {record.node && <Meta label="Узел" value={record.node} />}
        {record.logger && <Meta label="Логгер" value={record.logger} />}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={copyAsJson}>
          <Copy className="h-3 w-3 mr-1" /> Копировать как JSON
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => copy("line", cleanMessage)}
        >
          Копировать текст
        </Button>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
      <span className="font-mono text-foreground/80 truncate">{value}</span>
    </div>
  );
}
